/**
 * WebSocketSharedService - Production-ready WebSocket message routing service
 * 
 * This service focuses on message routing and communication with the SharedWorker.
 * Connection state management is handled by ConnectionStateService.
 * 
 * Key Features:
 * - Clean separation of concerns (messages vs state)
 * - Automatic reconnection handling
 * - Message queuing and delivery confirmation
 * - Integration with ConnectionStateService
 * - Production-ready error handling
 * 
 * @class WebSocketSharedService
 */
import {inject, Injectable, OnDestroy} from "@angular/core";
import {BehaviorSubject, forkJoin, fromEvent, interval, merge, Observable, of, Subject} from "rxjs";
import {catchError, distinctUntilChanged, map, takeUntil, throttleTime} from "rxjs/operators";
import {environment} from "../../../../environments/environment";
import {SharedService} from "../../../services/shared.service";
import {ConversationService} from "./conversation.service";
import {ChatStateService} from "../../core/services/chat-state.service";
import {StatusService} from "./status.service";
import {ConnectionStateService} from "../../core/services/connection-state.service";
import {
    ACK_MESSAGE_ACTION,
    ChatUser,
    CommonRecentChat,
    Group,
    WebSocketConnectionStatus
} from "../models/message.model";
import {createStatusUpdate, DISPATCHER_EVENT, IndicatorMessage, UserStatus} from "../models/conversation.model";
import {SecureStorageService} from "../../core/services/secure-storage.service";
import {LocalStorageRelatedData} from "../../../local-storage-related-data.model";

export interface ChatData {
    groups: Group[];
    recentChats: CommonRecentChat[];
    individualUsers: ChatUser[];
}

@Injectable({
    providedIn: 'root'
})
export class WebSocketSharedService implements OnDestroy {
    private static instanceCount = 0;
    private readonly instanceId: number;
    private readonly destroy$ = new Subject<void>();
    private secureStorageService = inject(SecureStorageService)
    // SharedWorker references
    private worker: SharedWorker | null = null;
    private port: MessagePort | null = null;
    private connectionId: string | null = null;
    private userId: number = 0;
    private isInitialized = false;
    private isInitializing = false;

    // Message handling
    private readonly incomingMessages$ = new Subject<any>();
    private readonly messageQueue$ = new Subject<any>();
    
    // Browser support and configuration
    private readonly useSharedWorker: boolean;
    private readonly debugMode: boolean;
    private readonly config = {
        workerPath: '/shared-websocket-worker.js',
        initTimeout: 10000,
        healthCheckInterval: 30000,
        wakeDetectionThreshold: 60000,
        heartbeatInterval: 30000 // Send heartbeat every 30 seconds
    };
    
    // Heartbeat management
    private heartbeatInterval: any = null;
    
    // Visibility tracking
    private isTabVisible: boolean = true;
    private lastVisibilityChangeTime: number = Date.now();

    // Initial data management
    private isInitialDataFetched = false;

    currentUserAccountId$ = new BehaviorSubject<number | null>(null);

    constructor(
        private sharedService: SharedService,
        private conversationService: ConversationService,
        private chatStateService: ChatStateService,
        private statusService: StatusService,
        private connectionStateService: ConnectionStateService
    ) {
        WebSocketSharedService.instanceCount++;
        this.instanceId = WebSocketSharedService.instanceCount;
        
        this.debugMode = !environment.production;
        this.useSharedWorker = this.checkSharedWorkerSupport();

        // console.log(`🏭 WebSocketSharedService v2.0.0 - Instance #${this.instanceId} - SharedWorker: ${this.useSharedWorker}`);
        
        if (this.instanceId > 1) {
            console.warn(`⚠️ Multiple WebSocketSharedService instances detected! This is instance #${this.instanceId}`);
        }

        this.initializeService();
    }

    /**
     * Checks if SharedWorker is supported in the current browser
     */
    private checkSharedWorkerSupport(): boolean {
        if (!('SharedWorker' in window)) {
            console.warn('❌ SharedWorker not supported in this browser');
            return false;
        }

        try {
            // Test SharedWorker availability (can be disabled in private mode)
            const testWorker = new SharedWorker('data:,');
            testWorker.port.close();
            return true;
        } catch (error) {
            console.warn('❌ SharedWorker disabled (possibly private mode):', error);
            return false;
        }
    }

    /**
     * Initializes the service and sets up message handling
     */
    private initializeService(): void {
        if (!this.useSharedWorker) {
            this.connectionStateService.setError('SharedWorker not supported');
            return;
        }

        this.checkInitialDataStatus();
        this.setupBrowserWakeDetection();
        this.setupVisibilityTracking();
        this.setupMessageHandling();
    }

    /**
     * Sets up Page Visibility API tracking to detect when tab is hidden/visible
     */
    private setupVisibilityTracking(): void {
        // Initialize visibility state
        this.isTabVisible = !document.hidden;
        this.lastVisibilityChangeTime = Date.now();
        
        // Listen for visibility changes
        fromEvent(document, 'visibilitychange')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.isTabVisible = !document.hidden;
                this.lastVisibilityChangeTime = Date.now();
                
                // console.log(`👁️ Tab visibility changed: ${this.isTabVisible ? 'visible' : 'hidden'}`);
                
                // Notify SharedWorker about visibility change
                if (this.isInitialized) {
                    this.sendToWorker({
                        type: 'VISIBILITY_CHANGE',
                        payload: {
                            isVisible: this.isTabVisible,
                            timestamp: this.lastVisibilityChangeTime,
                            connectionId: this.connectionId
                        }
                    });
                }

                // Reconnection on becoming visible is handled by the single throttled
                // merge(visibilitychange, focus, pageshow, online) subscription in
                // setupBrowserWakeDetection() — calling ensureConnection() here too would
                // race it and send duplicate CONNECT messages to the worker.
            });
        
        // console.log('👁️ Page Visibility API tracking initialized');
    }

    /**
     * Sets up browser wake detection to handle laptop sleep/wake scenarios
     */
    private setupBrowserWakeDetection(): void {
        let lastCheckTime = Date.now();

        // Check for browser wake every 30 seconds
        interval(30000)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                const now = Date.now();
                const timeSinceLastCheck = now - lastCheckTime;

                // If more than 2 minutes have passed, browser was likely asleep
                if (timeSinceLastCheck > 120000) {
                    // console.log(`🌅 Browser wake detected (${timeSinceLastCheck}ms gap)`);
                    this.handleBrowserWake();
                }

                lastCheckTime = now;
            });

        // Enhanced visibility change handler
        merge(
            fromEvent(document, 'visibilitychange'),
            fromEvent(window, 'focus'),
            fromEvent(window, 'pageshow'),
            fromEvent(window, 'online')
        ).pipe(
            throttleTime(1000),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            if (!document.hidden && navigator.onLine) {
                // console.log(`📱 Page became visible and online`);
                this.ensureConnection();
            }
        });
    }

    /**
     * Sets up message handling and worker communication
     */
    private setupMessageHandling(): void {
        // Process incoming messages and route them appropriately
        this.incomingMessages$
            .pipe(takeUntil(this.destroy$))
            .subscribe(messageData => {
                this.routeIncomingMessage(messageData);
            });

        // Monitor connection state changes
        this.connectionStateService.getConnectionState$()
            .pipe(
                takeUntil(this.destroy$),
                distinctUntilChanged()
            )
            .subscribe(state => {
                if (state === WebSocketConnectionStatus.CONNECTED && !this.isInitialDataFetched) {
                    this.fetchInitialChatData();
                }
            });
    }

    /**
     * Initializes SharedWorker and establishes communication
     */
    private async initSharedWorker(): Promise<boolean> {
        if (this.isInitialized || !this.useSharedWorker) {
            // console.log('⚠️ SharedWorker already initialized or not supported');
            return this.isInitialized;
        }

        // Prevent multiple initializations
        if (this.isInitializing) {
            // console.log('⚠️ Initialization already in progress');
            // Wait for initialization to complete
            return new Promise((resolve) => {
                const checkInterval = setInterval(() => {
                    if (!this.isInitializing) {
                        clearInterval(checkInterval);
                        resolve(this.isInitialized);
                    }
                }, 100);
            });
        }

        if (this.worker) {
            // console.log('⚠️ Worker already exists, skipping initialization');
            return this.isInitialized;
        }

        this.isInitializing = true;

        try {
            // console.log('🔧 Initializing SharedWorker...');
            
            this.worker = new SharedWorker(this.config.workerPath);
            this.port = this.worker.port;

            // Setup message handling
            this.port.onmessage = (event) => {
                this.handleWorkerMessage(event.data);
            };

            this.port.onmessageerror = (error) => {
                console.error('🚨 SharedWorker port error:', error);
                this.connectionStateService.setError('Worker communication error');
            };

            this.port.start();

            // Wait for worker ready confirmation
            return new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    this.isInitializing = false;
                    reject(new Error('Worker initialization timeout'));
                }, this.config.initTimeout);

                const handleReady = (event: MessageEvent) => {
                    if (event.data.type === 'WORKER_READY') {
                        clearTimeout(timeout);
                        this.port!.removeEventListener('message', handleReady);
                        this.connectionId = event.data.connectionId;
                        this.isInitialized = true;
                        this.isInitializing = false;

                        // console.log(`✅ SharedWorker ready - Connection ID: ${this.connectionId}`);
                        
                        // Start heartbeat to keep tab active
                        this.startHeartbeat();
                        
                        resolve(true);
                    }
                };

                this.port!.addEventListener('message', handleReady);
            });

        } catch (error) {
            console.error('❌ Failed to initialize SharedWorker:', error);
            this.isInitializing = false;
            let errorMessage = error instanceof Error ? error.message : 'Unknown error';
            this.connectionStateService.setError(`Worker initialization failed: ${errorMessage}`);
            return false;
        }
    }

    sendDeliveryAck(chat: any){
        if(!chat.messageId){
            console.warn("No messageId for deliveryAck");
            return;
        }


        // For direct messages, receiverId in the incoming message IS this user's account ID —
        // no need to wait for currentUserAccountId$ to be set by the UI.
        // For group messages, fall back to currentUserAccountId$.
        const ownAccountId = (!chat.groupId && chat.receiverId)
            ? chat.receiverId
            : this.currentUserAccountId$.value;

        if(!ownAccountId) return;

        // In the payload send receiverId or groupId based on the type of chat
        const ackMessage = {
            type: "MESSAGE_ACK",
            payload: {
                action: ACK_MESSAGE_ACTION.DELIVERY_ACK,
                messageId: chat.messageId,
                senderId: ownAccountId,
                ...(chat.groupId
                    ? {groupId: chat.groupId}
                    : {receiverId: chat.lastMessageSenderAccountId || chat.senderId})
            }

        };

        this.sendToWorker(ackMessage);
    }

    sendReadAck(chat: any, messageFromWebSocket: boolean): void {
        if (!chat?.lastMessageId && !messageFromWebSocket) {
            console.warn("No message ID available for read ACK");
            return;
        }

        const ownAccountId = this.currentUserAccountId$.value;

        if(!ownAccountId) return;


        // In the payload send receiverId or groupId based on the type of chat
        // For groupId we receive 2 responses one when we select a recent chat and one when we are in live chat. So 2 we have to check 2 responses to pass the groupId
        const ackMessage = {
            type: "MESSAGE_ACK",
            payload: {
                action: ACK_MESSAGE_ACTION.READ_ACK,
                messageId: chat.lastMessageId || chat.messageId,
                senderId: ownAccountId,
                ...(
                    chat.isGroup
                        ? { groupId: chat.entityId }
                        : chat.groupId
                            ? { groupId: chat.groupId }
                            : { receiverId: chat.lastMessageSenderAccountId || chat.senderId }
                )
            }
        };

        this.sendToWorker(ackMessage);
    }

    /**
     * Handles incoming messages from SharedWorker
     */
    private handleWorkerMessage(data: any): void {
        if (this.debugMode && data.type !== 'PONG') {
            // console.log(`📬 Worker message: ${data.type}`, data);
        }

        // Route message to ConnectionStateService
        this.connectionStateService.processWorkerMessage(data);

        // Handle specific message types
        switch (data.type) {
            case 'MESSAGE_RECEIVED':
                // console.log('🔵 WebSocketSharedService received MESSAGE_RECEIVED', {
                //     broadcastId: data.broadcastId,
                //     messageType: data.data?.messageType,
                //     timestamp: new Date().toISOString()
                // });
                const message = data.data;
                this.incomingMessages$.next(message);

                // Delivery ACKs are for chat messages only. Dispatcher events carry a senderId
                // (the actor) but no messageId, so they must not trigger an ACK.
                const isDispatcherEvent = message?.type === DISPATCHER_EVENT.TYPE;

                // if message senderId is not equal to currentUserId that is the user sending the ack message should not be the one who sent the message
                if(!isDispatcherEvent && message.senderId && this.currentUserAccountId$.value != message.senderId) {
                    this.sendDeliveryAck(message);
                }
                break;

            case 'MESSAGE_SENT':
                // console.log('✅ Message sent successfully');
                break;

            case 'MESSAGE_SEND_FAILED':
                // console.error('❌ Message send failed:', data.error);
                break;

            case 'MESSAGE_QUEUED':
                // console.log('📦 Message queued:', data.reason);
                break;

            case 'PING_CHECK':
                // Respond immediately to prove we're alive
                // console.log('🏓 Received PING_CHECK, responding with PONG_CHECK');
                this.sendToWorker({
                    type: 'PONG_CHECK',
                    payload: { timestamp: Date.now() }
                });
                break;

            default:
                // Other messages are handled by ConnectionStateService
                break;
        }
    }

    /**
     * Routes incoming messages to appropriate handlers
     */
    private routeIncomingMessage(messageData: any): void {
        try {
            // console.log('🟢 routeIncomingMessage called', {
            //     messageType: messageData?.messageType,
            //     messageId: messageData?.messageId,
            //     timestamp: new Date().toISOString()
            // });
            if (messageData.messageType !== 'INDICATOR') {
                // console.log('📨 Received message:', messageData);
            }

            // Handle status indicator messages
            if (messageData.messageType === 'INDICATOR') {
                this.statusService.handleIncomingMessage(messageData);
                return;
            }

            // Handle other message types here
            // This is where you'd add routing for different message types

        } catch (error) {
            console.error('❌ Error processing incoming message:', error);
        }
    }

    /**
     * Handles browser wake scenarios
     */
    private handleBrowserWake(): void {
        if (!this.isInitialized) return;

        this.requestConnectionState();

        // forceReconnect() resets the worker's exhausted retry counter, so this
        // recovers even if the automatic reconnect loop already gave up.
        setTimeout(() => {
            const currentState = this.connectionStateService.getConnectionInfo().state;
            if (currentState !== WebSocketConnectionStatus.CONNECTED) {
                this.forceReconnect();
            }
        }, 2000);
    }

    /**
     * Ensures connection is active
     */
    private ensureConnection(): void {
        if (!this.isInitialized) {
            this.connect();
            return;
        }

        const currentState = this.connectionStateService.getConnectionInfo().state;
        if (currentState === WebSocketConnectionStatus.DISCONNECTED || currentState === WebSocketConnectionStatus.ERROR) {
            // console.log('🔄 Connection not active - attempting connection');
            this.connect();
        }
    }

    /**
     * Sends message to SharedWorker
     */
    private sendToWorker(message: any): void {
        if (!this.port || !this.isInitialized) {
            console.warn('⚠️ Cannot send to worker: Not initialized');
            return;
        }

        try {
            this.port.postMessage(message);
        } catch (error) {
            console.error('❌ Failed to send message to worker:', error);
            this.connectionStateService.setError('Worker communication error');
        }
    }

    // Public API methods

    /**
     * Establishes WebSocket connection
     */
    public async connect(): Promise<void> {
        this.userId = this.sharedService.getLoggedInUserId();
        if (!this.userId) {
            this.connectionStateService.setError('No user ID available');
            return;
        }

        // Set currentUserAccountId$ early so delivery/read ACKs work even when
        // no chat has been selected yet. selected-chat-header will override this
        // with the org-specific ID once the user opens a conversation.
        if (!this.currentUserAccountId$.value) {
            const accountIds = this.sharedService.getAllAccountIdsOfLoggedInUser(false) as number[];
            if (accountIds?.length > 0) {
                this.currentUserAccountId$.next(accountIds[0]);
            }
        }

        try {
            const initialized = await this.initSharedWorker();
            if (!initialized) {
                this.connectionStateService.setError('Failed to initialize SharedWorker');
                return;
            }

            const wsUrl = this.buildWebSocketUrl();
            const token = this.sharedService.getLoggedInUserToken();
            const accountIds = this.sharedService.getAllAccountIdsOfLoggedInUser();

            // console.log(`🔗 Connecting user ${this.userId}...`);

            this.sendToWorker({
                type: 'CONNECT',
                payload: {
                    wsUrl,
                    userId: this.userId,
                    token,
                    accountIds,
                    metadata: {
                        userAgent: navigator.userAgent,
                        timestamp: Date.now()
                    }
                }
            });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            this.connectionStateService.setError(`Connection failed: ${errorMessage}`);
        }
    }

    /**
     * Closes WebSocket connection
     */
    public closeConnection(): void {
        // console.log('🔌 Closing connection...');
        
        if (this.isInitialized) {
            this.sendToWorker({
                type: 'DISCONNECT',
                payload: null
            });
        }

        this.connectionStateService.resetConnection();
    }

    public attemptReconnection(): void {
        if (!this.isInitialized) {
            this.connect();
            return;
        }
        this.forceReconnect(); // if the component is already initialized then we need to force reconnect
    }

    /**
     * Sends a message through WebSocket
     */
    public sendMessage(message: any): void {
        if (!this.isInitialized) {
            console.error('❌ Cannot send message: Service not initialized');
            return;
        }

        this.sendToWorker({
            type: 'SEND_MESSAGE',
            payload: message
        });
    }

    /**
     * Sends status indicator message
     */
    public sendStatusIndicatorMessage(status: UserStatus): void {
        const statusUpdateObject = createStatusUpdate(
            status.statusId,
            status.customStatusMessage,
            status.userId
        );

        const localStatus = {
            messageType: "INDICATOR",
            data: status,
        } as IndicatorMessage;

        // Send to server
        this.sendMessage(JSON.stringify(statusUpdateObject));

        // Update local status
        this.statusService.handleIncomingMessage(localStatus);
    }

    /**
     * Forces reconnection
     */
    public forceReconnect(): void {
        if (!this.isInitialized) {
            this.connect();
            return;
        }

        // console.log('🔧 Force reconnecting...');

        const wsUrl = this.buildWebSocketUrl();
        const token = this.sharedService.getLoggedInUserToken();

        this.sendToWorker({
            type: 'FORCE_RECONNECT',
            payload: {
                userId: this.userId,
                wsUrl,
                token
            }
        });
    }

    /**
     * Requests current connection state
     */
    public requestConnectionState(): void {
        if (this.isInitialized) {
            this.sendToWorker({
                type: 'GET_CONNECTION_STATE',
                payload: null
            });
        }
    }

    /**
     * Requests connection statistics
     */
    public getWorkerStats(): void {
        if (this.isInitialized) {
            this.sendToWorker({
                type: 'GET_STATS',
                payload: null
            });
        }
    }

    // Data fetching methods

    /**
     * Fetches initial chat data
     */
    public fetchChatData(): Observable<ChatData> {
        const allAccountIds = this.sharedService.getAllAccountIdsOfLoggedInUser() as string;
        const userId = this.sharedService.getLoggedInUserId();

        return forkJoin({
            groups: this.conversationService
                .getGroupsForUser(userId, allAccountIds)
                .pipe(catchError(() => of({data: []}))),
            recentChats: this.conversationService
                .getRecentMessagesV3(allAccountIds)
                .pipe(catchError(() => of({data: []}))),
            individualUsers: this.conversationService
                .getAllUsersForConversation(allAccountIds, undefined)
                .pipe(catchError(() => of({data: []}))),
        }).pipe(
            map(({groups, recentChats, individualUsers}) => {
                this.chatStateService.setInitialState(
                    recentChats.data,
                    individualUsers.data,
                    groups.data
                );
                return {
                    groups: groups.data,
                    recentChats: recentChats.data,
                    individualUsers: individualUsers.data,
                };
            }),
            catchError(() => {
                this.chatStateService.setInitialState([], [], []);
                return of({
                    groups: [],
                    recentChats: [],
                    individualUsers: [],
                });
            }),
        );
    }

    /**
     * Fetches initial chat data and marks it as fetched
     */
    private fetchInitialChatData(): void {
        if (this.isInitialDataFetched) return;

        // console.log('📥 Fetching initial chat data...');

        this.fetchChatData().subscribe({
            next: () => {
                this.isInitialDataFetched = true;
                this.markInitialDataAsFetched();
                // console.log('✅ Initial chat data loaded');
            },
            error: (error) => {
                console.error('❌ Failed to fetch initial chat data:', error);
            },
        });
    }

    /**
     * Marks initial data as fetched in localStorage
     */
    private markInitialDataAsFetched(): void {
        try {
            this.secureStorageService.setItem(LocalStorageRelatedData.CHAT_INITIAL_DATA_FETCHED , {
                userId: this.userId,
                timestamp: Date.now(),
            });
        } catch (error) {
            console.error('Failed to mark initial data as fetched:', error);
        }
    }

    /**
     * Checks if initial data was already fetched
     */
    private checkInitialDataStatus(): void {
        try {
            const dataFetchedInfo = this.secureStorageService.getItem<any>(LocalStorageRelatedData.CHAT_INITIAL_DATA_FETCHED);
            if (dataFetchedInfo) {
                const {userId} = dataFetchedInfo;
                this.isInitialDataFetched = userId === this.sharedService.getLoggedInUserId();
            }
        } catch (error) {
            console.error('Failed to parse initial data status:', error);
            this.secureStorageService.removeItem(LocalStorageRelatedData.CHAT_INITIAL_DATA_FETCHED);
        }
    }

    /**
     * Clears initial data flag
     */
    public clearInitialDataFlag(): void {
        this.isInitialDataFetched = false;
        try {
            this.secureStorageService.removeItem(LocalStorageRelatedData.CHAT_INITIAL_DATA_FETCHED);
        } catch (error) {
            console.error('Failed to clear initial data flag:', error);
        }
    }

    /**
     * Builds WebSocket URL with authentication
     */
    private buildWebSocketUrl(): string {
        const params = new URLSearchParams({
            userId: this.userId.toString(),
            accountIds: this.sharedService.getAllAccountIdsOfLoggedInUser() as string,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });

        const token = this.sharedService.getLoggedInUserToken();
        const socketBaseUrl = environment.webSocketUrl;

        return `${socketBaseUrl}?${params.toString()}&Authorization=Bearer ${encodeURIComponent(token)}`;
    }

    // Observable getters

    /**
     * Gets observable stream of incoming messages
     */
    public getMessages(): Observable<any> {
        return this.incomingMessages$.asObservable();
    }

    /**
     * Gets connection state observable (delegated to ConnectionStateService)
     */
    public getConnectionState$(): Observable<WebSocketConnectionStatus> {
        return this.connectionStateService.getConnectionState$();
    }


    // Debug and utility methods

    /**
     * Checks if using SharedWorker
     */
    public isUsingSharedWorker(): boolean {
        return this.useSharedWorker;
    }

    /**
     * Gets connection information for debugging
     */
    public getConnectionInfo(): any {
        const stateInfo = this.connectionStateService.getConnectionInfo();
        // return {
        //     useSharedWorker: this.useSharedWorker,
        //     isInitialized: this.isInitialized,
        //     connectionId: this.connectionId,
        //     userId: this.userId,
        //     isInitialDataFetched: this.isInitialDataFetched,
        //     ...stateInfo
        // };
    }

    /**
     * Gets connection summary for debugging
     */
    public getConnectionSummary(): any {
        return this.connectionStateService.getConnectionSummary();
    }

    /**
     * Starts sending periodic heartbeat messages to keep tab active
     */
    private startHeartbeat(): void {
        // Clear any existing heartbeat
        this.stopHeartbeat();

        // console.log('💓 Starting heartbeat interval');

        // Send initial heartbeat
        this.sendHeartbeat();

        // Setup periodic heartbeat
        this.heartbeatInterval = setInterval(() => {
            this.sendHeartbeat();
        }, this.config.heartbeatInterval);
    }

    /**
     * Stops the heartbeat interval
     */
    private stopHeartbeat(): void {
        if (this.heartbeatInterval) {
            // console.log('💔 Stopping heartbeat interval');
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    /**
     * Sends a heartbeat message to the SharedWorker
     */
    private sendHeartbeat(): void {
        if (!this.isInitialized) return;

        this.sendToWorker({
            type: 'HEARTBEAT',
            payload: {
                timestamp: Date.now(),
                connectionId: this.connectionId,
                isVisible: this.isTabVisible
            }
        });
    }

    ngOnDestroy(): void {
        // console.log('🏭 WebSocketSharedService destroying...');

        this.closeConnection();
        
        // Stop heartbeat
        this.stopHeartbeat();

        // Complete subjects
        this.destroy$.next();
        this.destroy$.complete();
        this.incomingMessages$.complete();
        this.messageQueue$.complete();

        // Close worker port
        if (this.port) {
            try {
                this.port.close();
            } catch (error) {
                console.error('Error closing worker port:', error);
            }
            this.port = null;
        }

        this.isInitialized = false;
        this.connectionId = null;
        this.clearInitialDataFlag();

        // console.log('🏭 WebSocketSharedService destroyed');
    }
}